import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { toast } from 'sonner';
import { employeeApi, extractErrorMessage } from '../../../services/hrApi';
import type { Employee } from '../../../types';
import './StepStyles.scss';

interface BasicDetailsStepProps {
  employeeId?: string;
  data?: any;
  onDataChange: (data: any) => void;
  onComplete: (isComplete: boolean) => void;
  onEmployeeCreated?: (employee: Employee) => void;
}

interface FormErrors {
  first_name?: string;
  last_name?: string;
  email?: string;
  phone?: string;
  date_of_birth?: string;
  employee_type?: string;
  classification?: string;
  department?: string;
  position?: string;
  salary?: string;
  salary_rate?: string;
}

const BasicDetailsStep: React.FC<BasicDetailsStepProps> = ({
  employeeId,
  data,
  onDataChange,
  onComplete,
  onEmployeeCreated,
}) => {
  const [formData, setFormData] = useState({
    first_name: data?.first_name || '',
    last_name: data?.last_name || '',
    email: data?.email || '',
    phone: data?.phone || '',
    date_of_birth: data?.date_of_birth || '',
    hire_date: data?.hire_date || new Date().toISOString().split('T')[0],
    department: data?.department || '',
    position: data?.position || '',
    employee_type: data?.employee_type || '',
    classification: data?.classification || '',
    salary: data?.salary !== undefined ? String(data.salary) : '',
    salary_rate: data?.salary_rate || '',
    address: data?.address || '',
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [localEmployeeId, setLocalEmployeeId] = useState<string | undefined>(employeeId);
  const touchedRef = useRef<Record<string, boolean>>({});

  // Update local employeeId when prop changes
  useEffect(() => {
    if (employeeId) {
      setLocalEmployeeId(employeeId);
    }
  }, [employeeId]);
  
  // Determine the current employee ID (prefer local, fallback to prop) - useMemo to make it reactive
  const currentEmployeeId = useMemo(() => {
    return localEmployeeId || employeeId;
  }, [localEmployeeId, employeeId]);

  // Validation functions - memoized to prevent re-renders
  const validateField = useCallback((field: string, value: any): string | undefined => {
    switch (field) {
      case 'first_name':
        if (!value || value.trim() === '') {
          return 'First name is required';
        }
        if (value.trim().length < 2) {
          return 'First name must be at least 2 characters';
        }
        if (!/^[a-zA-Z\s'-]+$/.test(value.trim())) {
          return 'First name can only contain letters, spaces, hyphens, and apostrophes';
        }
        break;

      case 'last_name':
        if (!value || value.trim() === '') {
          return 'Last name is required';
        }
        if (value.trim().length < 2) {
          return 'Last name must be at least 2 characters';
        }
        if (!/^[a-zA-Z\s'-]+$/.test(value.trim())) {
          return 'Last name can only contain letters, spaces, hyphens, and apostrophes';
        }
        break;

      case 'email':
        if (!value || value.trim() === '') {
          return 'Email is required';
        }
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value.trim())) {
          return 'Please enter a valid email address';
        }
        break;

      case 'phone':
        if (value && value.trim() !== '') {
          const digitsOnly = value.replace(/\D/g, '');
          if (digitsOnly.length !== 10) {
            return 'Phone number must be exactly 10 digits';
          }
        }
        break;

      case 'date_of_birth':
        if (value) {
          const birthDate = new Date(value);
          const today = new Date();
          if (birthDate > today) {
            return 'Date of birth cannot be in the future';
          }
        }
        break;

      case 'employee_type':
        if (!value || value.trim() === '') {
          return 'Employee type is required';
        }
        break;

      case 'classification':
        if (!value || value.trim() === '') {
          return 'Classification is required';
        }
        break;

      case 'department':
        if (!value || value.trim() === '') {
          return 'Department is required';
        }
        if (value.trim().length < 2) {
          return 'Department must be at least 2 characters';
        }
        break;

      case 'position':
        if (!value || value.trim() === '') {
          return 'Position is required';
        }
        if (value.trim().length < 2) {
          return 'Position must be at least 2 characters';
        }
        break;

      case 'salary':
        if (!value || value.toString().trim() === '') {
          return 'Salary is required';
        }
        const salaryNum = parseFloat(value.toString().replace(/,/g, ''));
        if (isNaN(salaryNum)) {
          return 'Salary must be a valid number';
        }
        if (salaryNum <= 0) {
          return 'Salary must be greater than 0';
        }
        break;

      case 'salary_rate':
        if (!value || value.toString().trim() === '') {
          return 'Salary rate is required';
        }
        const validRates = ['hr', 'week', 'month', 'yr'];
        if (!validRates.includes(value.toString().trim().toLowerCase())) {
          return 'Salary rate must be one of: hr, week, month, yr';
        }
        break;

      default:
        break;
    }
    return undefined;
  }, []);

  // Check if form is valid without setting errors (for button disabled state)
  const isFormValid = useCallback((): boolean => {
    const requiredFields: (keyof typeof formData)[] = [
      'first_name',
      'last_name',
      'email',
      'employee_type',
      'classification',
      'department',
      'position',
      'salary',
      'salary_rate',
    ];

    for (const field of requiredFields) {
      const error = validateField(field, formData[field]);
      if (error) {
        return false;
      }
    }

    // Validate optional fields if they have values
    if (formData.phone) {
      const phoneError = validateField('phone', formData.phone);
      if (phoneError) {
        return false;
      }
    }

    if (formData.date_of_birth) {
      const dobError = validateField('date_of_birth', formData.date_of_birth);
      if (dobError) {
        return false;
      }
    }

    return true;
  }, [formData, validateField]);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};
    let isValid = true;

    const requiredFields: (keyof typeof formData)[] = [
      'first_name',
      'last_name',
      'email',
      'employee_type',
      'classification',
      'department',
      'position',
      'salary',
      'salary_rate',
    ];

    requiredFields.forEach((field) => {
      const error = validateField(field, formData[field]);
      if (error) {
        newErrors[field] = error;
        isValid = false;
      }
    });

    // Validate optional fields if they have values
    if (formData.phone) {
      const phoneError = validateField('phone', formData.phone);
      if (phoneError) {
        newErrors.phone = phoneError;
        isValid = false;
      }
    }

    if (formData.date_of_birth) {
      const dobError = validateField('date_of_birth', formData.date_of_birth);
      if (dobError) {
        newErrors.date_of_birth = dobError;
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  // Validate a single touched field
  const validateTouchedField = useCallback((field: string) => {
    if (!touchedRef.current[field]) {
      return;
    }
    
    const error = validateField(field, formData[field as keyof typeof formData]);
    setErrors((prev) => {
      if (error) {
        return { ...prev, [field]: error };
      } else {
        const newErrors = { ...prev };
        delete newErrors[field as keyof FormErrors];
        return newErrors;
      }
    });
  }, [formData, validateField]);

  // Check completion status separately - only validate all fields, don't check errors for completion
  const isComplete = useMemo(() => {
    const hasAllRequiredFields =
      formData.first_name.trim() !== '' &&
      formData.last_name.trim() !== '' &&
      formData.email.trim() !== '' &&
      formData.employee_type !== '' &&
      formData.classification !== '' &&
      formData.department.trim() !== '' &&
      formData.position.trim() !== '' &&
      formData.salary.trim() !== '' &&
      formData.salary_rate.trim() !== '';

    if (!hasAllRequiredFields) {
      return false;
    }

    // Validate all fields to check if they're valid (but don't show errors unless touched)
    const requiredFields: (keyof typeof formData)[] = [
      'first_name',
      'last_name',
      'email',
      'employee_type',
      'classification',
      'department',
      'position',
      'salary',
      'salary_rate',
    ];

    for (const field of requiredFields) {
      const error = validateField(field, formData[field]);
      if (error) {
        return false;
      }
    }

    // Also check optional fields if they have values
    if (formData.phone && validateField('phone', formData.phone)) {
      return false;
    }
    if (formData.date_of_birth && validateField('date_of_birth', formData.date_of_birth)) {
      return false;
    }

    return true;
  }, [
    formData.first_name,
    formData.last_name,
    formData.email,
    formData.phone,
    formData.date_of_birth,
    formData.employee_type,
    formData.classification,
    formData.department,
    formData.position,
    formData.salary,
    formData.salary_rate,
    validateField,
  ]);

  const lastCompleteStatusRef = useRef<boolean | null>(null);
  
  useEffect(() => {
    // Only call onComplete if the completion status actually changed
    if (lastCompleteStatusRef.current !== isComplete) {
      lastCompleteStatusRef.current = isComplete;
      onComplete(isComplete);
    }
  }, [isComplete, onComplete]);

  const handleChange = (field: string, value: any) => {
    const updated = { ...formData, [field]: value };
    setFormData(updated);
    onDataChange(updated);

    // Mark field as touched when user starts typing
    if (!touched[field]) {
      const newTouched = { ...touched, [field]: true };
      setTouched(newTouched);
      touchedRef.current = newTouched;
    }
    
    // Validate the field after a short delay if it's been touched
    if (touched[field] || touchedRef.current[field]) {
      setTimeout(() => {
        const error = validateField(field, value);
        setErrors((prev) => {
          if (error) {
            return { ...prev, [field]: error };
          } else {
            const newErrors = { ...prev };
            delete newErrors[field as keyof FormErrors];
            return newErrors;
          }
        });
      }, 300);
    }
  };

  const handleBlur = (field: string) => {
    // Mark field as touched when user leaves it
    const newTouched = { ...touched, [field]: true };
    setTouched(newTouched);
    touchedRef.current = newTouched;
    
    // Validate the field
    const error = validateField(field, formData[field as keyof typeof formData]);
    setErrors((prev) => {
      if (error) {
        return { ...prev, [field]: error };
      } else {
        const newErrors = { ...prev };
        delete newErrors[field as keyof FormErrors];
        return newErrors;
      }
    });
  };

  const handleSaveEmployee = async () => {
    // Validate before saving
    if (!validateForm()) {
      setSaveError('Please fix all errors before saving');
      return;
    }

    setSaving(true);
    setSaveError(null);

    try {
      // Convert salary string to number
      const salaryValue = formData.salary ? parseFloat(formData.salary.replace(/,/g, '')) : undefined;
      
      const employeeData: any = {
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone || undefined,
        date_of_birth: formData.date_of_birth || undefined,
        hire_date: formData.hire_date,
        department: formData.department,
        position: formData.position,
        employee_type: formData.employee_type,
        classification: formData.classification,
        salary: salaryValue,
        salary_rate: formData.salary_rate,
        address: formData.address || undefined,
      };

      let employee: Employee;
      
      if (currentEmployeeId) {
        // Update existing employee
        employeeData.status = 'onboarding'; // Keep status as onboarding
        employee = await employeeApi.updateEmployee(currentEmployeeId, employeeData);
        
        // Show success notification for update
        toast.success('Employee updated successfully!', {
          description: `Changes to ${employee.first_name} ${employee.last_name} have been saved.`,
          duration: 4000,
        });
      } else {
        // Create new employee
        employeeData.status = 'onboarding';
        employee = await employeeApi.createEmployee(employeeData);
        
        // Extract employee ID - handle different response structures
        // The API returns response.data || response, so employee should be the Employee object directly
        const createdEmployeeId = employee?.id || (employee as any)?.id;
        
        if (!createdEmployeeId) {
          console.error('Employee created but no ID found in response:', employee);
          setSaveError('Employee created but failed to get ID. Please refresh the page.');
          setSaving(false);
          return;
        }
        
        // Update local state immediately so button text updates
        // Use functional update to ensure we get the latest state
        setLocalEmployeeId(() => createdEmployeeId);
        
        // Also clear any previous errors
        setSaveError(null);
        
        // Show success notification
        toast.success('Employee created successfully!', {
          description: `${employee.first_name} ${employee.last_name} has been added to the system.`,
          duration: 4000,
        });
        
        if (onEmployeeCreated) {
          onEmployeeCreated(employee);
        }
      }
    } catch (error: any) {
      console.error(`Error ${currentEmployeeId ? 'updating' : 'creating'} employee:`, error);
      
      // Extract error message and filter out technical details like "code 400"
      let errorMessage = extractErrorMessage(error);
      
      // Replace technical error messages with user-friendly message
      if (errorMessage.toLowerCase().includes('code 400') || 
          errorMessage.toLowerCase().includes('400') ||
          errorMessage.toLowerCase().includes('bad request')) {
        errorMessage = 'Something went wrong. Please try again.';
      }
      
      setSaveError(errorMessage || 'Something went wrong. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="wizard-step">
      <div className="step-header">
        <h4>Basic Employee Details</h4>
        <p>Enter the basic information for the new employee</p>
      </div>

      <div className="step-form">
        <div className="form-row">
          <div className="form-group">
            <label htmlFor="first_name">
              First Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="first_name"
              value={formData.first_name}
              onChange={(e) => handleChange('first_name', e.target.value)}
              onBlur={() => handleBlur('first_name')}
              placeholder="Enter first name"
              className={errors.first_name && touched.first_name ? 'error' : ''}
            />
            {errors.first_name && touched.first_name && <span className="field-error">{errors.first_name}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="last_name">
              Last Name <span className="required">*</span>
            </label>
            <input
              type="text"
              id="last_name"
              value={formData.last_name}
              onChange={(e) => handleChange('last_name', e.target.value)}
              onBlur={() => handleBlur('last_name')}
              placeholder="Enter last name"
              className={errors.last_name && touched.last_name ? 'error' : ''}
            />
            {errors.last_name && touched.last_name && <span className="field-error">{errors.last_name}</span>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="email">
              Email <span className="required">*</span>
            </label>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => handleChange('email', e.target.value)}
              onBlur={() => handleBlur('email')}
              placeholder="Enter email address"
              className={errors.email && touched.email ? 'error' : ''}
            />
            {errors.email && touched.email && <span className="field-error">{errors.email}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="phone">Phone</label>
            <input
              type="tel"
              id="phone"
              value={formData.phone}
              onChange={(e) => handleChange('phone', e.target.value)}
              onBlur={() => handleBlur('phone')}
              placeholder="Enter phone number"
              className={errors.phone && touched.phone ? 'error' : ''}
            />
            {errors.phone && touched.phone && <span className="field-error">{errors.phone}</span>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="employee_type">
              Employee Type <span className="required">*</span>
            </label>
            <select
              id="employee_type"
              value={formData.employee_type}
              onChange={(e) => handleChange('employee_type', e.target.value)}
              onBlur={() => handleBlur('employee_type')}
              className={errors.employee_type && touched.employee_type ? 'error' : ''}
            >
              <option value="">Select employee type</option>
              <option value="w2">W2 (Employee)</option>
              <option value="1099">1099 (Contractor)</option>
            </select>
            {errors.employee_type && touched.employee_type && <span className="field-error">{errors.employee_type}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="classification">
              Classification <span className="required">*</span>
            </label>
            <select
              id="classification"
              value={formData.classification}
              onChange={(e) => handleChange('classification', e.target.value)}
              onBlur={() => handleBlur('classification')}
              className={errors.classification && touched.classification ? 'error' : ''}
            >
              <option value="">Select classification</option>
              <option value="exempt">Exempt</option>
              <option value="non_exempt">Non-Exempt</option>
            </select>
            {errors.classification && touched.classification && <span className="field-error">{errors.classification}</span>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="department">
              Department <span className="required">*</span>
            </label>
            <input
              type="text"
              id="department"
              value={formData.department}
              onChange={(e) => handleChange('department', e.target.value)}
              onBlur={() => handleBlur('department')}
              placeholder="e.g., Engineering"
              className={errors.department && touched.department ? 'error' : ''}
            />
            {errors.department && touched.department && <span className="field-error">{errors.department}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="position">
              Position <span className="required">*</span>
            </label>
            <input
              type="text"
              id="position"
              value={formData.position}
              onChange={(e) => handleChange('position', e.target.value)}
              onBlur={() => handleBlur('position')}
              placeholder="Enter position"
              className={errors.position && touched.position ? 'error' : ''}
            />
            {errors.position && touched.position && <span className="field-error">{errors.position}</span>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="salary">
              Salary <span className="required">*</span>
            </label>
            <input
              type="number"
              id="salary"
              value={formData.salary}
              onChange={(e) => handleChange('salary', e.target.value)}
              onBlur={() => handleBlur('salary')}
              placeholder="Enter salary amount"
              min="0"
              step="0.01"
              className={errors.salary && touched.salary ? 'error' : ''}
            />
            {errors.salary && touched.salary && <span className="field-error">{errors.salary}</span>}
          </div>

          <div className="form-group salary-rate-group">
            <label htmlFor="salary_rate">
              Salary Rate <span className="required">*</span>
            </label>
            <select
              id="salary_rate"
              value={formData.salary_rate}
              onChange={(e) => handleChange('salary_rate', e.target.value)}
              onBlur={() => handleBlur('salary_rate')}
              className={errors.salary_rate && touched.salary_rate ? 'error' : ''}
            >
              <option value="">Select rate period</option>
              <option value="hr">hr</option>
              <option value="week">week</option>
              <option value="month">month</option>
              <option value="yr">yr</option>
            </select>
            {errors.salary_rate && touched.salary_rate && <span className="field-error">{errors.salary_rate}</span>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="date_of_birth">Date of Birth</label>
            <input
              type="date"
              id="date_of_birth"
              value={formData.date_of_birth}
              onChange={(e) => handleChange('date_of_birth', e.target.value)}
              onBlur={() => handleBlur('date_of_birth')}
              className={errors.date_of_birth && touched.date_of_birth ? 'error' : ''}
            />
            {errors.date_of_birth && touched.date_of_birth && <span className="field-error">{errors.date_of_birth}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="hire_date">Hire Date</label>
            <input
              type="date"
              id="hire_date"
              value={formData.hire_date}
              onChange={(e) => handleChange('hire_date', e.target.value)}
            />
          </div>
        </div>

        <div className="form-group full-width">
          <label htmlFor="address">Address</label>
          <textarea
            id="address"
            value={formData.address}
            onChange={(e) => handleChange('address', e.target.value)}
            placeholder="Enter address"
            rows={3}
          />
        </div>

        <div className="form-actions">
          {saveError && (
            <div className="error-message">
              <i className="bx bx-error-circle"></i>
              {saveError}
            </div>
          )}
          <button
            className="btn-save-employee"
            onClick={handleSaveEmployee}
            disabled={saving || !isFormValid()}
          >
            {saving ? (
              <>
                <i className="bx bx-loader-alt bx-spin"></i>
                  {currentEmployeeId ? 'Saving...' : 'Creating...'}
              </>
            ) : (
              <>
                <i className="bx bx-save"></i>
                {currentEmployeeId ? 'Save Changes' : 'Create Employee'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default BasicDetailsStep;
